import type { ColumnDef, TableRow } from '@/components/DataTable';
import scalpePro from '@/assets/products/scalpe-pro.png';
import hydraboost from '@/assets/products/hydraboost.png';
import revitalize from '@/assets/products/revitalize.png';
import brightenup from '@/assets/products/brightenup.png';
import purenature from '@/assets/products/purenature.png';
import deepclean from '@/assets/products/deepclean.png';

/**
 * Static fixture reproducing the Figma comp exactly (section 2161:7629).
 * Values, deltas and selection state are copied from the design so the
 * visual-regression suite can diff against the Figma export 1:1.
 *
 * Phase 2 swaps this module for the live API; the shapes below are the
 * contract the API layer must satisfy.
 */

const SPARK_HEIGHT_GROUP20 = 18.3634;
const SPARK_HEIGHT_GROUP19 = 15.6023;

/**
 * Vertex coordinates lifted verbatim from the Figma sparkline exports.
 * Every row in a column shares one shape in the comp, so these two series
 * reproduce the design exactly. Live data replaces them in phase 2, at which
 * point `seriesX` is dropped and the component spaces vertices evenly.
 */
const GROUP20_X = [2.5143, 23.6466, 45.4703, 69.3989, 94.7825, 113.676, 137.486];
const GROUP20_Y = [15.8491, 6.34892, 14.4012, 10.4389, 2.5143, 11.7597, 7.66969];
const GROUP19_X = [2.49524, 26.0917, 45.1247, 69.3439, 90.1278, 112.815, 137.505];
const GROUP19_Y = [13.1071, 6.30018, 10.3592, 8.92168, 2.49524, 9.04849, 8.92168];

const metric = (value: string, delta: string, direction: 'up' | 'down', variant: 20 | 19 = 20) => ({
  value,
  delta,
  direction,
  series: variant === 20 ? GROUP20_Y : GROUP19_Y,
  seriesX: variant === 20 ? GROUP20_X : GROUP19_X,
});

export const CATEGORY_COLUMNS: ColumnDef[] = [
  { key: 'label', header: 'Category', width: 161, kind: 'label' },
  { key: 'psl', help: 'psl', header: 'PSL', width: 157, kind: 'psl', info: true },
  { key: 'wt_osa', help: 'wt_osa', header: 'Wt OSA%', width: 164, kind: 'metric', info: true, sparkHeight: SPARK_HEIGHT_GROUP20, sparkStroke: 'var(--spark-stroke)' },
  { key: 'listing', help: 'listing', header: 'Listing%', width: 164, kind: 'metric', info: true, sparkHeight: SPARK_HEIGHT_GROUP20, sparkStroke: 'var(--spark-stroke)' },
  { key: 'osa_ls', help: 'osa_ls', header: 'OSA in LS%', width: 164, kind: 'metric', info: true, sparkHeight: SPARK_HEIGHT_GROUP19, sparkStroke: 'var(--spark-stroke-soft)' },
  { key: 'doi', help: 'doi', header: 'DOI', width: 157, kind: 'value', info: true },
  { key: 'soh', help: 'soh', header: 'SOH', width: 157, kind: 'value', info: true },
];

const CITY_METRIC_W = 130.667;
export const CITY_COLUMNS: ColumnDef[] = [
  { key: 'label', header: 'City', width: 161, kind: 'label' },
  { key: 'psl', help: 'psl', header: 'PSL', width: CITY_METRIC_W, kind: 'psl', info: true },
  { key: 'wt_osa', help: 'wt_osa', header: 'Wt OSA%', width: 164, kind: 'metric', info: true, sparkHeight: SPARK_HEIGHT_GROUP20, sparkStroke: 'var(--spark-stroke)' },
  { key: 'listing', help: 'listing', header: 'Listing%', width: 164, kind: 'metric', info: true, sparkHeight: SPARK_HEIGHT_GROUP20, sparkStroke: 'var(--spark-stroke)' },
  { key: 'osa_ls', help: 'osa_ls', header: 'OSA in LS%', width: 164, kind: 'metric', info: true, sparkHeight: SPARK_HEIGHT_GROUP19, sparkStroke: 'var(--spark-stroke-soft)' },
  { key: 'doi', help: 'doi', header: 'DOI', width: CITY_METRIC_W, kind: 'value', info: true },
  { key: 'soh', help: 'soh', header: 'SOH', width: CITY_METRIC_W, kind: 'value', info: true },
  { key: 'actions', header: 'Actions', width: 79, kind: 'actions' },
];

export const SKU_COLUMNS: ColumnDef[] = [
  { key: 'label', header: 'SKU', width: 279, kind: 'label' },
  { key: 'psl', help: 'psl', header: 'PSL', width: 100, kind: 'psl', info: true },
  { key: 'wt_osa', help: 'wt_osa', header: 'Wt OSA%', width: 164, kind: 'metric', info: true, sparkHeight: SPARK_HEIGHT_GROUP20, sparkStroke: 'var(--spark-stroke)' },
  { key: 'listing', help: 'listing', header: 'Listing%', width: 164, kind: 'metric', info: true, sparkHeight: SPARK_HEIGHT_GROUP20, sparkStroke: 'var(--spark-stroke)' },
  { key: 'osa_ls', help: 'osa_ls', header: 'OSA in LS%', width: 164, kind: 'metric', info: true, sparkHeight: SPARK_HEIGHT_GROUP19, sparkStroke: 'var(--spark-stroke-soft)' },
  { key: 'doi', help: 'doi', header: 'DOI', width: 102, kind: 'value', info: true },
  { key: 'soh', help: 'soh', header: 'SOH', width: 72, kind: 'value', info: true },
  { key: 'actions', header: 'Actions', width: 79, kind: 'actions' },
];

/** Shared metric spine — identical across the three tables in the comp. */
const SPINE = [
  { osa: '72.0%', d1: '(+1.5%)', ls: '2.1%', d3: '(+0.5%)', dir3: 'up' as const, doi: '12 days', soh: '150' },
  { osa: '70.5%', d1: '(+2.5%)', ls: '1.6%', d3: '(+0.4%)', dir3: 'up' as const, doi: '8 days', soh: '200' },
  { osa: '73.8%', d1: '(+1.8%)', ls: '2.7%', d3: '(+0.6%)', dir3: 'up' as const, doi: '14 days', soh: '121' },
  { osa: '75.4%', d1: '(+1.6%)', ls: '3.3%', d3: '(+0.6%)', dir3: 'up' as const, doi: '10 days', soh: '55' },
  { osa: '76.1%', d1: '(+0.7%)', ls: '3.8%', d3: '(+0.5%)', dir3: 'up' as const, doi: '20 days', soh: '60' },
  { osa: '77.3%', d1: '(+1.2%)', ls: '4.2%', d3: '(+0.4%)', dir3: 'up' as const, doi: '13 days', soh: '65' },
  { osa: '78.5%', d1: '(+1.2%)', ls: '5.0%', d3: '(+0.8%)', dir3: 'up' as const, doi: '16 days', soh: '70' },
];

const PSL_ORDER = ['Medium', 'High', 'High', 'Medium', 'Medium', 'Low', 'Low'] as const;

function spineCells(i: number, doiDelta?: { text: string; dir: 'up' | 'down' }) {
  const s = SPINE[i];
  return {
    wt_osa: metric(s.osa, s.d1, 'up', 20),
    listing: metric(s.osa, s.d1, 'up', 20),
    osa_ls: metric(s.ls, s.d3, s.dir3, 19),
    doi: doiDelta
      ? { value: s.doi, delta: doiDelta.text, direction: doiDelta.dir }
      : { value: s.doi },
    soh: { value: s.soh },
  };
}

const CATEGORY_META = [
  { label: 'All', pills: undefined },
  { label: 'Shampoo', pills: [{ icon: 'share' as const, text: '32%' }, { icon: 'count' as const, text: '9' }] },
  { label: 'Conditioner', pills: [{ icon: 'share' as const, text: '28%' }, { icon: 'count' as const, text: '8' }] },
  { label: 'Body Wash', pills: [{ icon: 'share' as const, text: '20%' }, { icon: 'count' as const, text: '11' }] },
  { label: 'Face Wash', pills: [{ icon: 'share' as const, text: '10%' }, { icon: 'count' as const, text: '6' }] },
  { label: 'Hair Gel', pills: [{ icon: 'share' as const, text: '7%' }, { icon: 'count' as const, text: '12' }] },
  { label: 'Moisturizer', pills: [{ icon: 'share' as const, text: '3%' }, { icon: 'count' as const, text: '10' }] },
];

/** Category DOI carries a delta; the third row's OSA-in-LS goes negative in the comp. */
const CATEGORY_DOI: Array<{ text: string; dir: 'up' | 'down' }> = [
  { text: '(+4)', dir: 'up' }, { text: '(+4)', dir: 'up' }, { text: '(-4)', dir: 'down' },
  { text: '(+4)', dir: 'up' }, { text: '(-4)', dir: 'down' }, { text: '(-4)', dir: 'down' },
  { text: '(+4)', dir: 'up' },
];

export const CATEGORY_ROWS: TableRow[] = CATEGORY_META.map((m, i) => {
  const cells = spineCells(i, CATEGORY_DOI[i]);
  if (i === 4) cells.osa_ls = metric('3.8%', '(-0.5%)', 'down', 19);
  return {
    id: `category-${m.label}`,
    label: m.label,
    pills: m.pills,
    psl: PSL_ORDER[i],
    selected: i === 1,
    cells,
  };
});

const CITY_META = [
  { label: 'All', share: undefined },
  { label: 'Mumbai', share: '32%' },
  { label: 'Delhi', share: '28%' },
  { label: 'Hyderabad', share: '20%' },
  { label: 'Banglore', share: '10%' },
  { label: 'Chennai', share: '7%' },
  { label: 'Pune', share: '3%' },
];

export const CITY_ROWS: TableRow[] = CITY_META.map((m, i) => ({
  id: `city-${m.label}`,
  label: m.label,
  pills: m.share ? [{ icon: 'share' as const, text: m.share }] : undefined,
  psl: PSL_ORDER[i],
  selected: i === 1,
  cells: spineCells(i),
}));

const SKU_META = [
  { label: 'All', image: undefined, size: undefined, share: undefined },
  { label: 'Scalpe Pro Anti Dandruff Sha..', image: scalpePro, size: '200ml', share: '35%' },
  { label: 'HydraBoost Moisturizing Cream', image: hydraboost, size: '200ml', share: '35%' },
  { label: 'Revitalize Hair Repair Conditioner', image: revitalize, size: '250ml', share: '40%' },
  { label: 'BrightenUp Vitamin C Serum', image: brightenup, size: '30ml', share: '50%' },
  { label: 'PureNature Organic Face Wash', image: purenature, size: '150ml', share: '30%' },
  { label: 'DeepClean Charcoal Peel Off Mask', image: deepclean, size: '100ml', share: '45%' },
];

const SKU_DOI: Array<{ text: string; dir: 'up' | 'down' }> = [
  { text: '(-4)', dir: 'down' }, { text: '(-4)', dir: 'down' }, { text: '(-4)', dir: 'down' },
  { text: '(+4)', dir: 'up' }, { text: '(-4)', dir: 'down' }, { text: '(+4)', dir: 'up' },
  { text: '(+4)', dir: 'up' },
];

export const SKU_ROWS: TableRow[] = SKU_META.map((m, i) => ({
  id: `sku-${m.label}`,
  label: m.label,
  image: m.image,
  pillGap: 10,
  pills:
    m.size && m.share
      ? [{ icon: 'size' as const, text: m.size }, { icon: 'share' as const, text: m.share }]
      : undefined,
  psl: PSL_ORDER[i],
  selected: i === 1,
  cells: spineCells(i, SKU_DOI[i]),
}));
